import { useMutation } from '@apollo/client';
import {
  ActiveLaneIcon,
  AlertService,
  Button,
  Checkbox,
  ColorBadge,
  DeliveryIcon,
  DryIcon,
  FrozenIcon,
  icons,
  PickupIcon,
  RefrigeratedIcon,
  SideDropArrowIcon,
  SpoilerAlertAppleIcon,
  ActiveLaneTruckIcon,
  InactiveLaneTruckIcon,
  DeliveryTruckIcon,
  SnowflakeIcon,
  DryBoxIcon,
  RefrigeratorIcon,
  DollarCircleIcon,
  Theme,
  Timing,
} from '@spoiler-alert/ui-library';
import accounting from 'accounting';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { offerlistingStatuses, subscriptionTier } from '../../enums';
import { IgnoreOfferListings, RestoreOfferListings } from '../../graphql/mutations';
import { OfferListingsByInventoryQuery, trucklanePollingQuery, trucklaneExceptionsQuery } from '../../graphql/queries';
import routePaths from '../../route-paths';
import { Store } from '../../store';
import Tracking from '../../utilities/tracking-apis';
import TrucklaneCardSummary from './trucklane-card-summary';
import { TrucklaneActionInProgressContext } from './trucklane-utility';
import { ACTIVE_STEP } from '../offer-comparison/active-step-enums';
import { checkFeature } from '../../helpers';
import { featureFlags } from '../../enums';
import DisplayBuyerMinimums from './buyer-minimum-display';

const styles = {
  card: (selected) => ({
    border: `1px solid ${selected ? Theme.green : Theme.grey30}`,
    backgroundColor: selected ? Theme.green5 : Theme.white,
    borderRadius: '4px',
    display: 'flex',
    justifyContent: 'space-between',
    margin: '12px 0px',
    transition: '500ms ease-in-out',
    transitionProperty: 'transform opacity',
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: Theme.grey5,
      border: `1px solid ${Theme.greyDark}`,
    },
  }),
  checkboxWrapper: {
    height: '50px',
  },
  checkbox: {
    padding: '16px',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  cardOverview: {
    display: 'flex',
    flexDirection: 'column',
    padding: '16px 24px 24px 16px',
    '& h1': {
      fontSize: '14px',
      fontWeight: '500',
      margin: 0,
    },
    '& svg': {
      marginRight: '8px',
    },
    '& h2': {
      fontSize: '18px',
      fontWeight: 'normal',
      margin: 0,
    },
  },
  overviewLogistics: {
    display: 'flex',
    alignItems: 'center',
    '& p': {
      margin: 0,
      fontSize: '16px',
      color: Theme.grey,
      fontWeight: 'normal',
      marginRight: 4,
    },
  },
  overviewActions: {
    marginTop: 'auto',
    display: 'flex',
    '& button': {
      marginRight: 8,
    },
  },
  row: {
    display: 'flex',
  },
  rowBreak: {
    marginTop: '12px',
  },
  logisticsIcon: {
    height: 16,
    width: 16,
    fill: Theme.grey,
  },
  activeLane: {
    border: `1px solid ${Theme.primaryColor}`,
    backgroundColor: Theme.green5,
    borderRadius: '2px',
    marginRight: 12,
    display: 'flex',
    alignItems: 'center',
    '& p': {
      color: Theme.primaryColor,
      width: 'fit-content',
      padding: '2px 8px 2px 0px',
      marginBottom: 0,
      fontSize: '0.75rem',
      fontWeight: 500,
      lineHeight: '0.75rem',
      whiteSpace: 'nowrap',
    },
    '& svg': {
      stroke: Theme.primaryColor,
      fill: 'transparent',
      width: 20,
      height: 20,
      marginLeft: 8,
    },
  },
  buyerNameContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
  },
  spoilerAlertApple: {
    height: 18,
    width: 16,
  },
  // rpe enabled styles
  leftContainer: {
    flex: '3 1 380px',
    display: 'flex',
    minWidth: 0,
  },
  cardV2: (selected) => ({
    border: `1px solid ${selected ? Theme.green : Theme.grey30}`,
    backgroundColor: selected ? Theme.green5 : Theme.white,
    borderRadius: '6px',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    margin: '12px 0px',
    transition: '500ms ease-in-out',
    transitionProperty: 'transform opacity',
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: Theme.grey5,
      border: `1px solid ${Theme.greyDark}`,
    },
  }),
  trucklaneBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 14,
    fontWeight: '500',
    lineHeight: '16px',
    '& svg': {
      marginRight: 6,
    },
  },
  activeTruckLane: {
    extend: 'trucklaneBadge',
    color: Theme.primaryColor,
  },
  spaceBetween: {
    justifyContent: 'space-between',
  },
  truckData: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    margin: '24px 0px',
    fontSize: 16,
    fontWeight: '400',
    gap: 12,
    '& svg': {
      margin: 0,
    },
  },
  truckDataBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  buyerSiteName: {
    fontStyle: 'normal',
    fontWeight: 500,
    letterSpacing: -0.096,
    fontSize: '16px !important',
  },
  logisticsDestination: { letterSpacing: '-0.198px' },
};

const TrucklaneCard = ({
  offers,
  activeStep,
  recalculation,
  trucklaneId,
  selected,
  handleSelect,
  awarded,
  deSelect,
  user,
  openDrilldown,
  buyerMinimums,
  history,
}) => {
  const useStyles = createUseStyles(styles);
  const classes = useStyles(selected);
  const [awardSuggestions, setAwardSuggestions] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const { setActionInProgress } = useContext(TrucklaneActionInProgressContext);
  const aggregateOffer = offers[0];
  const activeTruckLane = offers.filter((offer) => [offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED].includes(offer.status));
  const ignoredOffers = offers.filter((offer) => [offerlistingStatuses.IGNORED].includes(offer.status));
  const saasLiteCustomer = user.site.subscriptionTier === subscriptionTier.saasLite;
  // Quoted as a rate for one truck on every tab; Truck Count carries how many trucks the lane needs.
  const perTruckCost = aggregateOffer.trucklaneCost || 0;
  const [ignoreOffersMutation, { loading: ignoreOffersLoading }] = useMutation(IgnoreOfferListings);
  const [restoreOffersMutation, { loading: restoreOffersLoading }] = useMutation(RestoreOfferListings);
  const reservePriceExceptionsEnabled = checkFeature(featureFlags.reservePriceExceptions);

  const logisticsIcons = {
    Pickup: PickupIcon,
    Delivery: DeliveryIcon,
  };

  const logisticsIconsV2 = {
    Pickup: PickupIcon,
    Delivery: DeliveryTruckIcon,
  };

  const truckTypeIcons = {
    Dry: DryIcon,
    Refrigerated: RefrigeratedIcon,
    Frozen: FrozenIcon,
  };

  const truckTypeIconsV2 = {
    Dry: DryBoxIcon,
    Refrigerated: RefrigeratorIcon,
    Frozen: SnowflakeIcon,
  };

  const LogisticsIcon = reservePriceExceptionsEnabled ? logisticsIconsV2[aggregateOffer.logisticsTerm] : logisticsIcons[aggregateOffer.logisticsTerm];
  const TruckIcon = reservePriceExceptionsEnabled ? truckTypeIconsV2[aggregateOffer.truckType] : truckTypeIcons[aggregateOffer.truckType];

  const displayLogisticsTerm = () => {
    return aggregateOffer.logisticsTerm === 'Pickup' ? (
      <div className={classes.overviewLogistics}>
        <LogisticsIcon className={classes.logisticsIcon} />
        <p>{aggregateOffer.logisticsTerm}</p>
      </div>
    ) : (
      <div className={classes.overviewLogistics}>
        <LogisticsIcon className={classes.logisticsIcon} />
        <p>
          {aggregateOffer.logisticsTerm} - {accounting.formatNumber(aggregateOffer.trucklaneDistance)} miles
        </p>
        {!saasLiteCustomer && <p>{accounting.formatMoney(perTruckCost)} Est. Cost / Truck</p>}
      </div>
    );
  };

  const ignoreOffers = Timing.throttle((e) => {
    e.stopPropagation();
    setActionInProgress(true);
    const matchedOffers = offers.filter((offer) => offer.status === offerlistingStatuses.ACTIVE);
    setActionLoading(true);
    if (activeStep === ACTIVE_STEP.SUGGESTIONS) deSelect(trucklaneId);
    ignoreOffersMutation({
      variables: { offerListingIds: matchedOffers.map((offer) => offer._id) },
      refetchQueries: [
        {
          query: trucklanePollingQuery,
          variables: {
            pollingDetails: [...Store.trucklaneChannels.values()].map((channel) => ({
              origin: channel.originDCId,
              truckType: channel.truckType,
            })),
          },
        },
        {
          query: OfferListingsByInventoryQuery,
          variables: { inventoryIds: matchedOffers.map((offer) => offer.inventoryId) },
        },
        { query: trucklaneExceptionsQuery },
      ],
    }).then((ignoreResp) => {
      setActionLoading(false);
      if (ignoreResp.data?.ignoreOfferListingsById?.errors?.length)
        AlertService.alert({
          type: 'warning',
          autoDismiss: true,
          message: <span>{ignoreResp.data?.ignoreOfferListingsById?.errors[0].message}</span>,
        });
      Tracking.Event('Offers were ignored in trucklane view', {
        userId: user._id,
      });
      setActionInProgress(false);
    });
  }, 1000);

  const restoreOffers = Timing.throttle((e) => {
    e.stopPropagation();
    setActionInProgress(true);
    const matchedOffers = ignoredOffers;
    setActionLoading(true);
    if (activeStep === ACTIVE_STEP.SUGGESTIONS) deSelect(trucklaneId);
    restoreOffersMutation({
      variables: { offerListingIds: matchedOffers.map((offer) => offer._id) },
      refetchQueries: [
        {
          query: trucklanePollingQuery,
          variables: {
            pollingDetails: [...Store.trucklaneChannels.values()].map((channel) => ({
              origin: channel.originDCId,
              truckType: channel.truckType,
            })),
          },
        },
        {
          query: OfferListingsByInventoryQuery,
          variables: { inventoryIds: matchedOffers.map((offer) => offer.inventoryId) },
        },
        { query: trucklaneExceptionsQuery },
      ],
    }).then((restoreResp) => {
      setActionLoading(false);
      if (restoreResp.data?.restoreOfferListingsById?.errors?.length)
        AlertService.alert({
          type: 'warning',
          autoDismiss: true,
          message: <span>{restoreResp.data?.restoreOfferListingsById?.errors[0].message}</span>,
        });
      Tracking.Event('Offers were restored in trucklane view', {
        userId: user._id,
      });
      setActionInProgress(false);
    });
  }, 1000);

  const ignoreOffersDisabler = () => {
    return (
      ignoredOffers.length === offers.length ||
      activeTruckLane.length === offers.length ||
      activeTruckLane.length + ignoredOffers.length === offers.length ||
      restoreOffersLoading ||
      ignoreOffersLoading ||
      actionLoading
    );
  };

  const restoreOffersEnabler = () => {
    return !ignoredOffers.length || activeTruckLane.length === offers.length || ignoreOffersLoading || actionLoading;
  };

  const slideCard = () => {
    setAwardSuggestions(true);
  };

  const checkboxDisabled = () => {
    if (activeStep === ACTIVE_STEP.SUGGESTIONS) {
      return recalculation;
    }
    if (activeStep === ACTIVE_STEP.OPPORTUNITIES) {
      return (
        recalculation || !offers?.some((offer) => offer.suggestions?.reservePriceException?.suggested && offer.status === offerlistingStatuses.ACTIVE)
      );
    }
    return !offers?.filter((offer) => offer.status === offerlistingStatuses.AWARDED).length;
  };

  const getLaneStatus = () => {
    return activeTruckLane.length > 0 ? (
      <div className={classes.activeTruckLane}>
        <ActiveLaneTruckIcon /> <span>ACTIVE LANE</span>
      </div>
    ) : (
      <div className={classes.trucklaneBadge}>
        <InactiveLaneTruckIcon /> <span>INACTIVE LANE</span>
      </div>
    );
  };

  const displayTruckData = () => {
    return (
      <>
        <div className={`${classes.overviewLogistics} ${classes.rowBreak}`}>
          <TruckIcon className={classes.logisticsIcon} />
          <p data-testid={'card-truck-type'}>{aggregateOffer.truckType}</p>
        </div>
        <div className={classes.row}>
          {displayLogisticsTerm()}
          {
            <DisplayBuyerMinimums
              buyerMinimums={buyerMinimums}
              trucklaneMinimums={user?.site?.trucklaneMinimums}
              aggregateOffer={aggregateOffer}
              reservePriceExceptionsEnabled={reservePriceExceptionsEnabled}
            />
          }
        </div>
      </>
    );
  };

  const displayTruckDataInRow = () => {
    return (
      <div className={`${classes.truckData}`}>
        <div className={classes.truckDataBox}>
          <TruckIcon className={classes.logisticsIcon} />
          <span data-testid={'card-truck-type'}>{aggregateOffer.truckType}</span>
        </div>
        {aggregateOffer.logisticsTerm === 'Pickup' ? (
          <div className={classes.truckDataBox}>
            <LogisticsIcon className={classes.logisticsIcon} />
            <span>{aggregateOffer.logisticsTerm}</span>
            {
              <DisplayBuyerMinimums
                buyerMinimums={buyerMinimums}
                trucklaneMinimums={user?.site?.trucklaneMinimums}
                aggregateOffer={aggregateOffer}
                reservePriceExceptionsEnabled={reservePriceExceptionsEnabled}
              />
            }
          </div>
        ) : (
          <>
            <div className={classes.truckDataBox}>
              <LogisticsIcon className={classes.logisticsIcon} />
              <span>
                {aggregateOffer.logisticsTerm} - {accounting.formatNumber(aggregateOffer.trucklaneDistance)} miles
              </span>
            </div>
            {!saasLiteCustomer && (
              <span className={classes.truckDataBox}>
                <DollarCircleIcon /> {accounting.formatMoney(perTruckCost)} Est. Cost / Truck
              </span>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div
      data-testid="trucklane-card"
      className={reservePriceExceptionsEnabled ? classes.cardV2 : classes.card}
      style={awardSuggestions || awarded ? { opacity: '0%' } : {}}
      onClick={() => {
        openDrilldown(trucklaneId);
        history.push({
          pathname: `${routePaths.offerComparison}/trucklane/${trucklaneId}`,
          search: history.location.search,
        });
      }}
    >
      <div className={reservePriceExceptionsEnabled ? classes.leftContainer : classes.row}>
        <div onClick={(e) => e.stopPropagation()} className={classes.checkboxWrapper}>
          {activeStep !== ACTIVE_STEP.INACTIVE_LANES && (
            <Checkbox className={classes.checkbox} checked={selected} onChecked={handleSelect} value={trucklaneId} disabled={checkboxDisabled()} />
          )}
        </div>
        <div className={`${classes.cardOverview} ${reservePriceExceptionsEnabled ? classes.spaceBetween : ''}`}>
          <div>
            <div className={classes.buyerNameContainer}>
              <h1 className={reservePriceExceptionsEnabled ? classes.buyerSiteName : ''}>{aggregateOffer.buyerSiteName}</h1>
              {aggregateOffer.isVendorOfRecord && <SpoilerAlertAppleIcon className={classes.spoilerAlertApple} />}
            </div>
            <h2 className={reservePriceExceptionsEnabled ? classes.logisticsDestination : ''}>{aggregateOffer.inventory?.siteName}</h2>
            <div className={classes.row}>
              <SideDropArrowIcon />
              <h2 className={reservePriceExceptionsEnabled ? classes.logisticsDestination : ''}>{aggregateOffer.buyerDestinationName}</h2>
            </div>
            {reservePriceExceptionsEnabled ? displayTruckDataInRow() : displayTruckData()}
            <div className={`${classes.row} ${classes.rowBreak}`}>
              {!reservePriceExceptionsEnabled && activeTruckLane.length > 0 && (
                <div className={classes.activeLane}>
                  <ActiveLaneIcon /> <p>ACTIVE TRUCK LANE</p>
                </div>
              )}
              {ignoredOffers.length > 0 && !reservePriceExceptionsEnabled && <ColorBadge theme={'grey'}>IGNORED OFFERS</ColorBadge>}
            </div>
          </div>
          {reservePriceExceptionsEnabled && getLaneStatus()}
          {reservePriceExceptionsEnabled ? null : (
            <div className={classes.overviewActions}>
              <Button
                disabled={ignoreOffersDisabler()}
                loadingText="Ignoring Offers"
                loading={ignoreOffersLoading}
                onClick={(e) => ignoreOffers(e)}
                icon={icons.ignore}
                secondary
              >
                Ignore Offers
              </Button>
              <Button
                disabled={restoreOffersEnabler()}
                loadingText="Restoring Offers"
                loading={restoreOffersLoading}
                onClick={(e) => restoreOffers(e)}
                icon={icons.restore}
                secondary
              >
                Restore Offers
              </Button>
            </div>
          )}
        </div>
      </div>
      <TrucklaneCardSummary
        trucklaneId={trucklaneId}
        offers={offers}
        activeStep={activeStep}
        recalculation={recalculation}
        awardSuggestion={slideCard}
        selected={selected}
        deSelect={deSelect}
        actionLoading={actionLoading}
        childAction={setActionLoading}
        user={user}
        ignoreOffers={ignoreOffers}
        ignoreOffersLoading={ignoreOffersLoading}
        restoreOffers={restoreOffers}
        restoreOffersLoading={restoreOffersLoading}
        buyerMinimums={buyerMinimums}
      />
    </div>
  );
};

TrucklaneCard.propTypes = {
  offers: PropTypes.any,
  recalculation: PropTypes.bool,
  trucklaneId: PropTypes.string,
  selected: PropTypes.bool,
  handleSelect: PropTypes.func,
  deSelect: PropTypes.func,
  awarded: PropTypes.bool,
  user: PropTypes.object,
  openDrilldown: PropTypes.func,
  history: PropTypes.object,
  activeStep: PropTypes.string,
  buyerMinimums: PropTypes.object,
};

export default TrucklaneCard;
